import { MinusIcon, PlusIcon } from '@assets/icons';
import { useTranslation } from '@shared/core';
import { Box, Flex, TextV2, useDisclosure } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import React from 'react';
import type { FaqItem } from './useStationeryFaqItems';

const FaqElement: React.FC<FaqItem> = ({ question, answer }) => {
  const accordion = useDisclosure();
  const Icon = accordion.isOpen ? MinusIcon : PlusIcon;

  return (
    <Flex
      borderBottom="1px solid"
      borderBottomColor="mono3"
      width="100%"
      flexDirection="column"
    >
      <Flex
        as="button"
        onClick={() => accordion.onToggle()}
        padding={6}
        gap={6}
        alignItems="center"
      >
        <Icon
          width={pxToRem(24)}
          height={pxToRem(24)}
        />
        <TextV2
          typographyVariant="body2"
          fontWeight={600}
        >
          {question}
        </TextV2>
      </Flex>
      <Flex
        flexDirection="column"
        height={accordion.isOpen ? 'auto' : 0}
        overflow="hidden"
        paddingLeft={pxToRem(72)}
        paddingRight={6}
      >
        <TextV2
          paddingBottom={6}
          typographyVariant="body2"
        >
          {answer}
        </TextV2>
      </Flex>
    </Flex>
  );
};

type FaqListProps = Readonly<{
  children: React.ReactNode;
}>;

const FaqList: React.FC<FaqListProps> = ({ children }) => {
  const { tq } = useTranslation('stationery');

  return (
    <Box
      width="100%"
      maxWidth={pxToRem(1248)}
      paddingX={6}
      paddingY={10}
      marginX="auto"
      display="grid"
      gridTemplateColumns={['1fr', null, '1fr 2fr']}
      gap={9}
    >
      <Flex
        flexDirection="column"
        gap={6}
      >
        <TextV2
          textAlign={['center', null, 'left']}
          typographyVariant="hed6"
          fontWeight={600}
        >
          {tq`Frequently Asked Questions`}
        </TextV2>
        <TextV2
          textAlign={['center', null, 'left']}
          typographyVariant="body2"
          maxWidth={pxToRem(629)}
        >
          {tq`Take a look at our most commonly asked questions and answers. For additional support, chat with us.`}
        </TextV2>
      </Flex>
      <Flex
        flexDirection="column"
        borderTop="1px solid"
        borderTopColor="mono3"
      >
        {children}
      </Flex>
    </Box>
  );
};

type FaqSectionProps = Readonly<{
  faqItems: ReadonlyArray<FaqItem>;
}>;

export const FaqSection: React.FC<FaqSectionProps> = ({ faqItems }) => {
  if (faqItems.length === 0) {
    return null;
  }

  return (
    <FaqList>
      {faqItems.map((item, index) => (
        <FaqElement
          key={index}
          question={item.question}
          answer={item.answer}
        />
      ))}
    </FaqList>
  );
};
